import { institutionKeys } from "@features/institutions"
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query"

import {
  schoolsApi,
  subjectsApi,
  type ListSchoolsParams,
  type CreateSubjectPayload,
  type UpdateSchoolPayload,
  type UpdateSubjectPayload,
} from "./api"
import { schoolKeys, subjectKeys } from "./queryKeys"

import type { EvaluationCalendar, EvaluationPeriodIn } from "@/types"

export const useSchools = (params: ListSchoolsParams, enabled = true) =>
  useQuery({
    queryKey: schoolKeys.list(params),
    queryFn: () => schoolsApi.list(params),
    enabled,
    // Previous rows bridge a search or a page, never the trash boundary or
    // another institution (see professors/hooks.ts).
    placeholderData: (prev, prevQuery) => {
      const before = prevQuery?.queryKey[2] as ListSchoolsParams | undefined
      return before?.view === params.view && before?.institutionId === params.institutionId
        ? prev
        : undefined
    },
  })

// Every live school of one institution, for selects and counts.
export const useSchoolOptions = (institutionId: string | undefined) =>
  useQuery({
    queryKey: schoolKeys.options(institutionId ?? ""),
    queryFn: () => schoolsApi.options(institutionId as string),
    enabled: !!institutionId,
  })

// PRD-56 §FR-11 — one school, trashed or not, with its figures at the cut.
export const useSchool = (id: string | undefined) =>
  useQuery({
    queryKey: schoolKeys.detail(id ?? ""),
    queryFn: () => schoolsApi.get(id as string),
    enabled: !!id,
  })

// PRD-61 FR-1.5 — see institutions/hooks.ts::useInstitutionCalendar for why
// the previous year's payload stays up while another loads.
export const useSchoolCalendar = (id: string | undefined, schoolYear: string | null) =>
  useQuery({
    queryKey: schoolKeys.calendar(id ?? "", schoolYear),
    queryFn: () => schoolsApi.calendar(id as string, schoolYear),
    enabled: !!id,
    placeholderData: (prev) => prev,
  })

// PUT and DELETE both answer with the campus's effective calendar; every
// cached copy of that year takes it, whether it was asked for by name or as
// "the year in course". Synchronous, so the page never shows the set a
// DELETE just removed.
function storeSchoolCalendar(qc: QueryClient, id: string, saved: EvaluationCalendar) {
  qc.setQueriesData<EvaluationCalendar>({ queryKey: schoolKeys.calendarsFor(id) }, (old) =>
    old?.school_year === saved.school_year ? saved : old,
  )
  void qc.invalidateQueries({ queryKey: institutionKeys.healths() })
}

export const useSaveSchoolCalendar = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      schoolYear,
      periods,
    }: {
      id: string
      schoolYear: string
      periods: EvaluationPeriodIn[]
    }) => schoolsApi.saveCalendar(id, schoolYear, periods),
    onSuccess: (saved, { id }) => storeSchoolCalendar(qc, id, saved),
  })
}

export const useResetSchoolCalendar = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, schoolYear }: { id: string; schoolYear: string }) =>
      schoolsApi.resetCalendar(id, schoolYear),
    onSuccess: (saved, { id }) => storeSchoolCalendar(qc, id, saved),
  })
}

export const useCreateSchool = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: schoolsApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: schoolKeys.all })
      // The institution list shows school_count, which just changed.
      qc.invalidateQueries({ queryKey: institutionKeys.all })
    },
  })
}

export const useUpdateSchool = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSchoolPayload }) =>
      schoolsApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: schoolKeys.all }),
  })
}

// PRD-55 — logo upload/removal. Both invalidate the SCHOOL list (the row
// carries logo_path + logo_url) AND the institution list, because a
// school that clears its override immediately starts displaying the
// institution's crest and the form previews that inherited value.
export const useUploadSchoolLogo = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => schoolsApi.uploadLogo(id, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: schoolKeys.all })
      void qc.invalidateQueries({ queryKey: institutionKeys.all })
    },
  })
}

export const useRemoveSchoolLogo = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => schoolsApi.removeLogo(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: schoolKeys.all })
      void qc.invalidateQueries({ queryKey: institutionKeys.all })
    },
  })
}

export const useDeleteSchool = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => schoolsApi.remove(id),
    // Cascade soft-delete touches the school's professors too — drop the whole
    // admin cache so dependent lists refetch.
    onSuccess: () => qc.invalidateQueries(),
  })
}

export const useRestoreSchool = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => schoolsApi.restore(id),
    onSuccess: () => qc.invalidateQueries(),
  })
}

export const useSchoolSubjects = (schoolId: string | undefined, includeDeleted = false) =>
  useQuery({
    queryKey: subjectKeys.list(schoolId ?? "", includeDeleted),
    queryFn: () => subjectsApi.list(schoolId as string, includeDeleted),
    enabled: !!schoolId,
  })

export const useSeedDefaultSubjects = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (schoolId: string) => subjectsApi.seedDefaults(schoolId),
    onSuccess: (_created, schoolId) =>
      qc.invalidateQueries({ queryKey: subjectKeys.listsForSchool(schoolId) }),
  })
}

export const useCreateSubject = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ schoolId, data }: { schoolId: string; data: CreateSubjectPayload }) =>
      subjectsApi.create(schoolId, data),
    onSuccess: (_subject, { schoolId }) =>
      qc.invalidateQueries({ queryKey: subjectKeys.listsForSchool(schoolId) }),
  })
}

export const useUpdateSubject = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; schoolId: string; data: UpdateSubjectPayload }) =>
      subjectsApi.update(id, data),
    onSuccess: (_subject, { schoolId }) =>
      qc.invalidateQueries({ queryKey: subjectKeys.listsForSchool(schoolId) }),
  })
}

export const useDeleteSubject = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id }: { id: string; schoolId: string }) => subjectsApi.remove(id),
    onSuccess: (_data, { schoolId }) =>
      qc.invalidateQueries({ queryKey: subjectKeys.listsForSchool(schoolId) }),
  })
}

export const useRestoreSubject = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id }: { id: string; schoolId: string }) => subjectsApi.restore(id),
    onSuccess: (_data, { schoolId }) =>
      qc.invalidateQueries({ queryKey: subjectKeys.listsForSchool(schoolId) }),
  })
}
